import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { 
  MdArrowBack, 
  MdSave,
  MdAdd,
  MdDelete
} from 'react-icons/md';
import SubjectScheduleMatrix from './SubjectScheduleMatrix';
import AddSubjectModal from './AddSubjectModal';
import SharedClassBadge from './SharedClassBadge';

/**
 * Create Timetable Component
 * Form to create a new timetable
 */
function CreateTimetable() {
  const navigate = useNavigate();
  const { getToken } = useAttendanceAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [activeTermConfig, setActiveTermConfig] = useState(null);
  const autoPopulateKeyRef = useRef('');

  // Form data
  const [formData, setFormData] = useState({
    faculty_id: '', // Single faculty for single-faculty admins
    faculty_ids: [], // Multiple faculties for multi-faculty admins
    department_id: '',
    campus: '',
    batch_ids: [],
    semester: '',
    shift_ids: [],
    start_date: '',
    end_date: '',
    subjects: []
  });

  // Options data
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    if (formData.shift_ids.length > 0) {
      loadAllPeriods();
    } else {
      setPeriods([]);
    }
  }, [formData.shift_ids]);

  // Load departments for the currently selected faculty.
  useEffect(() => {
    const hasFaculty = faculties.length === 1
      ? Boolean(formData.faculty_id)
      : formData.faculty_ids.length === 1;
    if (hasFaculty) {
      loadDepartmentsByFaculty();
    } else {
      setDepartments([]);
      setFormData(prev => prev.department_id || prev.batch_ids.length
        ? { ...prev, department_id: '', batch_ids: [] }
        : prev);
    }
  }, [formData.faculty_id, formData.faculty_ids, faculties.length]);

  // A timetable always filters batches through a department.
  useEffect(() => {
    if (formData.department_id && formData.campus) {
      loadBatchesByDepartment();
    } else {
      setBatches([]);
      setFormData(prev => prev.batch_ids.length ? { ...prev, batch_ids: [] } : prev);
    }
  }, [formData.department_id, formData.campus, formData.faculty_id, formData.faculty_ids, faculties.length]);

  useEffect(() => {
    if (formData.campus && formData.batch_ids.length > 0 && formData.semester) {
      loadSubjects();
    }
  }, [formData.campus, formData.batch_ids, formData.semester]);
  useEffect(() => {
    const ready = Boolean(formData.campus)
      && formData.batch_ids.length > 0
      && Boolean(formData.semester)
      && formData.shift_ids.length > 0
      && periods.length > 0;
    if (!ready) {
      autoPopulateKeyRef.current = '';
      setLoading(false);
      setFormData(prev => prev.subjects.length ? { ...prev, subjects: [] } : prev);
      return;
    }

    const selectionKey = [
      formData.campus,
      [...formData.batch_ids].sort((a, b) => a - b).join(','),
      formData.semester,
      [...formData.shift_ids].sort((a, b) => a - b).join(','),
    ].join('|');
    if (autoPopulateKeyRef.current === selectionKey) return;

    autoPopulateKeyRef.current = selectionKey;
    handleAutoPopulate(selectionKey);
  }, [formData.campus, formData.batch_ids, formData.semester, formData.shift_ids, periods.length]);

  const loadOptions = async () => {
    try {
      const token = getToken();
      const [facultiesRes, campusesRes, yearsRes, shiftsRes, weekdaysRes, termConfigRes] = await Promise.all([
        attendanceAPI.getFaculties(token),
        attendanceAPI.getCampuses(token),
        attendanceAPI.getAcademicYears(token),
        attendanceAPI.getShifts(token),
        attendanceAPI.getWeekdays(token),
        attendanceAPI.getActiveTermConfig(token)
      ]);

      if (campusesRes.success) setCampuses(campusesRes.campuses || []);

      if (facultiesRes.success) {
        const facultiesList = facultiesRes.faculties || [];
        console.log('✅ Loaded faculties:', {
          count: facultiesList.length,
          faculties: facultiesList.map(f => ({ id: f.id, name: f.name || f.code }))
        });
        setFaculties(facultiesList);
        
        // BACKWARD COMPATIBILITY: If only one faculty, auto-select it
        // This maintains the old behavior for single-faculty admins (with department step)
        if (facultiesList.length === 1) {
          console.log('Single faculty detected, auto-selecting:', facultiesList[0].id);
          setFormData(prev => ({
            ...prev,
            faculty_id: String(facultiesList[0].id), // Auto-select single faculty (old behavior)
            faculty_ids: [] // Clear multi-faculty selection
          }));
        } else if (facultiesList.length > 1) {
          console.log(`Multi-faculty admin detected: ${facultiesList.length} faculties assigned. User can select multiple faculties.`);
          setFormData(prev => ({
            ...prev,
            faculty_id: '', // Clear single faculty selection
            faculty_ids: [] // Start with no faculties selected
          }));
        } else {
          console.warn('⚠️ No faculties returned from API. This might indicate a backend issue.');
        }
      } else {
        console.error('❌ Failed to load faculties:', facultiesRes.message || 'Unknown error');
      }
      
      if (yearsRes.success) setAcademicYears(yearsRes.academic_years || []);
      if (shiftsRes.success) setShifts(shiftsRes.shifts || []);
      if (weekdaysRes.success) setWeekdays(weekdaysRes.weekdays || []);

      if (termConfigRes.success && termConfigRes.start_date && termConfigRes.end_date) {
        setActiveTermConfig(termConfigRes);
        setFormData(prev => ({
          ...prev,
          start_date: termConfigRes.start_date,
          end_date: termConfigRes.end_date
        }));
      } else {
        setActiveTermConfig(null);
        setError(termConfigRes.message || 'No active term configuration found. Please contact administration.');
      }
    } catch (err) {
      console.error('Error loading options:', err);
      setError(err.message || 'Failed to load form options');
    }
  };

  const loadDepartmentsByFaculty = async () => {
    try {
      const selectedFacultyId = faculties.length === 1
        ? formData.faculty_id
        : formData.faculty_ids[0];
      if (!selectedFacultyId) {
        setDepartments([]);
        return;
      }
      
      const token = getToken();
      const facultyId = parseInt(selectedFacultyId);
      if (isNaN(facultyId)) {
        setError('Invalid faculty ID');
        setDepartments([]);
        return;
      }
      
      console.log('Loading departments for faculty:', facultyId);
      const departmentsRes = await attendanceAPI.getDepartments({ faculty_id: facultyId }, token);
      
      if (departmentsRes.success) {
        setDepartments(departmentsRes.departments || []);
        console.log(`Loaded ${departmentsRes.departments?.length || 0} departments for faculty ${facultyId}`);
      } else {
        setDepartments([]);
        setError(departmentsRes.message || 'Failed to load departments for selected faculty');
      }
    } catch (err) {
      console.error('Error loading departments by faculty:', err);
      setDepartments([]);
      setError(err.message || 'Failed to load departments');
    }
  };

  const loadBatchesByDepartment = async () => {
    try {
      if (!formData.department_id || !formData.campus) {
        setBatches([]);
        return;
      }
      
      const selectedFacultyId = faculties.length === 1
        ? formData.faculty_id
        : formData.faculty_ids[0];
      if (!selectedFacultyId) {
        setError('Faculty must be selected first');
        setBatches([]);
        return;
      }
      
      const token = getToken();
      const departmentId = parseInt(formData.department_id);
      const facultyId = parseInt(selectedFacultyId);
      
      if (isNaN(departmentId) || isNaN(facultyId)) {
        setError('Invalid department or faculty ID');
        setBatches([]);
        return;
      }
      
      const params = { department_id: departmentId, faculty_id: facultyId, campus: formData.campus };
      console.log('Loading batches for department:', departmentId, 'faculty:', facultyId);
      const batchesRes = await attendanceAPI.getBatches(params, token);
      
      if (batchesRes.success) {
        setBatches(batchesRes.batches || []);
        console.log(`Loaded ${batchesRes.batches?.length || 0} batches for department ${departmentId}`);
      } else {
        setBatches([]);
        setError(batchesRes.message || 'Failed to load batches for selected department');
      }
    } catch (err) {
      console.error('Error loading batches by department:', err);
      setBatches([]);
      setError(err.message || 'Failed to load batches');
    }
  };

  /**
   * Extract period number from period name (e.g., "Period 1" -> 1, "Period 10" -> 10)
   * Used for sorting periods by period number, not by sequence or start_time
   */
  const extractPeriodNumber = (period) => {
    const match = period.name?.match(/period\s*(\d+)/i);
    return match ? parseInt(match[1]) : 999; // Fallback to 999 if no number found
  };

  const loadAllPeriods = async () => {
    try {
      const token = getToken();
      const allPeriods = [];
      
      // Load periods for all selected shifts
      for (const shiftId of formData.shift_ids) {
        const result = await attendanceAPI.getPeriods(shiftId, token);
        if (result.success && result.periods) {
          // Add shift info to each period
          // ⚠️ IMPORTANT: Periods from API are already sorted by period number
          // We just add shift info and combine them
          const periodsWithShift = result.periods.map(period => ({
            ...period,
            shift_id: shiftId,
            shift_name: shifts.find(s => s.id === shiftId)?.name || shifts.find(s => s.id === shiftId)?.code
          }));
          allPeriods.push(...periodsWithShift);
        }
      }
      
      // ⚠️ CRITICAL: Sort by period number (extracted from name), NOT by sequence or start_time
      // Periods are already sorted correctly from API, but when combining multiple shifts,
      // we need to sort globally by period number to maintain correct order
      // Example: Period 1 (Morning), Period 2 (Morning), Period 3 (Morning), Period 4 (Afternoon)
      allPeriods.sort((a, b) => {
        return extractPeriodNumber(a) - extractPeriodNumber(b);
      });
      
      setPeriods(allPeriods);
    } catch (err) {
      console.error('Error loading periods:', err);
      setPeriods([]);
    }
  };

  const loadSubjects = async () => {
    try {
      const token = getToken();
      const result = await attendanceAPI.getSubjects({
        batch_ids: formData.batch_ids,
        semester: formData.semester,
        campus: formData.campus,
        faculty_id: Number(selectedFacultyId)
      }, token);
      if (result.success) {
        setSubjects(result.subjects || []);
      }
    } catch (err) {
      console.error('Error loading subjects:', err);
    }
  };

  const handleAutoPopulate = async (selectionKey) => {
    try {
      setLoading(true);
      const token = getToken();
      const result = await attendanceAPI.autoPopulateSubjects(
        formData.batch_ids,
        formData.semester,
        formData.shift_ids[0],
        formData.campus,
        selectedFacultyId,
        token
      );

      if (autoPopulateKeyRef.current !== selectionKey) return;
      if (result.success && result.subjects) {
        const subjectsWithSchedule = result.subjects.map(subject => {
          const subjectWithSchedule = { ...subject };
          periods.forEach((_, periodIdx) => {
            subjectWithSchedule[`period_${periodIdx + 1}_days`] = [];
          });
          return subjectWithSchedule;
        });
        setFormData(prev => ({ ...prev, subjects: subjectsWithSchedule }));
      } else {
        setError(result.message || 'Failed to load subjects automatically');
      }
    } catch (err) {
      if (autoPopulateKeyRef.current === selectionKey) {
        console.error('Error auto-populating subjects:', err);
        setError(err.message || 'Failed to load subjects automatically');
      }
    } finally {
      if (autoPopulateKeyRef.current === selectionKey) setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!activeTermConfig) {
      setError('No active term configuration found. Please contact administration.');
      return;
    }

    // Validation based on single vs multi-faculty admin
    if (faculties.length === 1) {
      // Single-faculty admin: require faculty_id and department_id
      if (!formData.faculty_id) {
        setError('Faculty not selected. Please refresh the page.');
        return;
      }
      if (!formData.department_id) {
        setError('Please select a department');
        return;
      }
    } else if (faculties.length > 1) {
      if (formData.faculty_ids.length !== 1) {
        setError('Please select one faculty');
        return;
      }
      if (!formData.department_id) {
        setError('Please select a department');
        return;
      }
    }

    if (!formData.campus) {
      setError('Please select a campus');
      return;
    }
    if (!formData.batch_ids.length) {
      setError('Please select one batch');
      return;
    }
    if (!formData.semester) {
      setError('Please fill in all required fields');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      setError('Please select date range');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = getToken();
      // Format subjects for API
      const formattedSubjects = formData.subjects.map(subject => {
        const formatted = {
          subject_id: subject.subject_id || subject.id
        };
        
        // Add period days arrays
        periods.forEach((_, periodIdx) => {
          const periodNum = periodIdx + 1;
          const periodDaysKey = `period_${periodNum}_days`;
          formatted[periodDaysKey] = subject[periodDaysKey] || [];
        });
        
        return formatted;
      });

      // Note: academic_year_id is not used by the API, so we don't include it
      // Backend model expects shift_id (singular), not shift_ids
      // Use the first selected shift ID
      if (!formData.shift_ids || formData.shift_ids.length === 0) {
        setError('Please select at least one shift');
        return;
      }

      const payload = {
        faculty_id: Number(selectedFacultyId),
        campus: formData.campus,
        batch_ids: formData.batch_ids,
        semester: formData.semester,
        // Send as shift_ids array (as guide specifies) - Many2many field requires array format
        // For single shift: [1], for multiple shifts: [1, 2]
        shift_ids: formData.shift_ids, // Already an array from form state
        subjects: formattedSubjects
      };

      // Log the payload for debugging
      console.log('Creating timetable - Payload being sent:', JSON.stringify(payload, null, 2));
      console.log('Payload keys:', Object.keys(payload));
      console.log('Has shift_ids?', 'shift_ids' in payload);
      console.log('Has shift_id?', 'shift_id' in payload);

      const result = await attendanceAPI.createTimetable(payload, token);
      
      // Debug: Log the full response structure
      console.log('Create timetable response:', result);
      console.log('Response keys:', Object.keys(result || {}));
      console.log('Has timetable?', !!result?.timetable);
      console.log('Timetable object:', result?.timetable);
      console.log('Timetable ID:', result?.timetable?.id);

      if (result.success) {
        // Extract timetable ID from response (according to guide lines 1169-1211)
        // Response structure: result.timetable.id
        const timetableId = result.timetable?.id;
        
        // Validate the ID before using it
        if (!timetableId || isNaN(parseInt(timetableId))) {
          console.error('Invalid timetable ID in response:', timetableId);
          console.error('Full response:', result);
          console.error('Response structure:', {
            success: result.success,
            hasTimetable: !!result.timetable,
            timetableKeys: result.timetable ? Object.keys(result.timetable) : [],
            timetableId: result.timetable?.id,
            timetable_id: result.timetable_id // Check if it's at root level
          });
          
          // Try alternative: check if ID is at root level (result.timetable_id)
          const altId = result.timetable_id;
          if (altId && !isNaN(parseInt(altId))) {
            console.warn('Found timetable_id at root level, using it:', altId);
            setTimeout(() => {
              navigate(`/attendance/faculty-admin/timetable/${altId}`);
            }, 1500);
            setSuccess(true);
            return;
          }
          
          setError('Failed to get timetable ID from response. Timetable was created but cannot navigate to view it.');
          setSaving(false);
          return;
        }
        
        setSuccess(true);
        console.log('Timetable created successfully with ID:', timetableId);
        
        // Navigate to view the created timetable
        setTimeout(() => {
          navigate(`/attendance/faculty-admin/timetable/${timetableId}`);
        }, 1500);
      } else {
        // Handle error according to guide - Error #34: Create Timetable Error (Server Error)
        // Error Response Format (from guide line 2430-2443):
        // {
        //   "success": false,
        //   "error": "Server Error",
        //   "message": "Failed to create timetable: [detailed error message]"
        // }
        const errorMessage = result.message || 'Failed to create timetable';
        setError(errorMessage);
        
        // If error mentions 'shift_ids' field, the backend model hasn't been updated yet
        // According to guide (line 1141), the model should have shift_ids (Many2many field)
        // This is a backend issue that needs to be fixed on the server side
        if (errorMessage.includes('shift_ids') || errorMessage.includes('Invalid field')) {
          console.error('Backend model issue: The model field shift_ids (Many2many) may not exist yet.');
          console.error('According to guide, backend should have shift_ids field. Backend team needs to add this field.');
        }
      }
    } catch (err) {
      console.error('Error creating timetable:', err);
      // Handle according to guide - Error #34: Create Timetable Error (Server Error)
      const errorMessage = err.message || 'Failed to create timetable';
      setError(errorMessage);
      
      // Check if it's the shift_ids field error - backend model needs to be updated
      if (errorMessage.includes('shift_ids') || errorMessage.includes('Invalid field')) {
        console.error('Backend model field issue detected. Guide specifies shift_ids (Many2many) should exist.');
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleBatch = (batch) => {
    if (batch.has_timetable) {
      setFormData(prev => ({
        ...prev,
        batch_ids: [],
        semester: '',
        shift_ids: [],
        subjects: []
      }));
      setError(`Batch "${batch.name || batch.code}" already has a timetable for the current academic term. Please edit the existing timetable instead.`);
      return;
    }

    setError(null);
    setFormData(prev => ({
      ...prev,
      // Each timetable belongs to exactly one batch.
      batch_ids: prev.batch_ids.includes(batch.id) ? [] : [batch.id],
      semester: '',
      shift_ids: [],
      subjects: []
    }));
  };

  const selectedFacultyId = faculties.length === 1
    ? formData.faculty_id
    : formData.faculty_ids[0] || '';
  const selectedBatchId = formData.batch_ids[0] || '';
  const setupReady = Boolean(
    formData.department_id
    && formData.campus
    && selectedBatchId
    && formData.semester
    && formData.shift_ids.length
  );
  const selectClassName = 'w-full h-11 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/attendance/faculty-admin/timetables')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-4 transition"
        >
          <MdArrowBack />
          <span>Back to Timetables</span>
        </button>

        <h1 className="text-2xl font-bold text-gray-800">Create New Timetable</h1>
        <p className="text-gray-600 text-sm mt-1">Create a new timetable for your faculty batches</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-semibold">
            {formData.subjects.length > 0 
              ? 'Subjects auto-populated successfully!'
              : 'Timetable created successfully! Redirecting...'}
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-1 border-b border-gray-100 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Timetable Setup</h2>
              <p className="mt-0.5 text-sm text-gray-500">Choose each filter from left to right.</p>
            </div>
            {faculties.length === 1 && selectedFacultyId && (
              <p className="text-sm text-gray-600">
                Faculty: <span className="font-semibold text-gray-800">{faculties[0]?.name || faculties[0]?.code}</span>
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 xl:grid-cols-5">
            {faculties.length > 1 && (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Faculty <span className="text-red-500">*</span></label>
                <select
                  value={selectedFacultyId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    faculty_ids: e.target.value ? [parseInt(e.target.value)] : [],
                    department_id: '', campus: '', batch_ids: [], semester: '', shift_ids: [], subjects: []
                  }))}
                  className={selectClassName}
                  required
                >
                  <option value="">Select Faculty</option>
                  {faculties.map(faculty => <option key={faculty.id} value={faculty.id}>{faculty.name || faculty.code}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Department <span className="text-red-500">*</span></label>
              <select
                value={formData.department_id}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  department_id: e.target.value,
                  campus: '',
                  batch_ids: [], semester: '', shift_ids: [], subjects: []
                }))}
                disabled={!selectedFacultyId}
                className={selectClassName}
                required
              >
                <option value="">{selectedFacultyId ? 'Select Department' : 'Select faculty first'}</option>
                {departments.map(dept => <option key={dept.id} value={dept.id}>{dept.name || dept.code}</option>)}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Campus <span className="text-red-500">*</span></label>
              <select
                value={formData.campus}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  campus: e.target.value,
                  batch_ids: [], semester: '', shift_ids: [], subjects: []
                }))}
                disabled={!formData.department_id}
                className={selectClassName}
                required
              >
                <option value="">{formData.department_id ? 'Select Campus' : 'Select department first'}</option>
                {campuses.map(campus => <option key={campus.id} value={campus.id}>{campus.name}</option>)}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Batch <span className="text-red-500">*</span></label>
              <select
                value={selectedBatchId}
                onChange={(e) => {
                  const batch = batches.find(item => String(item.id) === e.target.value);
                  if (batch) toggleBatch(batch);
                }}
                disabled={!formData.department_id || !formData.campus}
                className={selectClassName}
                required
              >
                <option value="">{formData.campus ? 'Select Batch' : 'Select campus first'}</option>
                {batches.map(batch => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name || batch.code}{batch.has_timetable ? ' — Already has a timetable' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Semester <span className="text-red-500">*</span></label>
              <select
                value={formData.semester}
                onChange={(e) => setFormData(prev => ({ ...prev, semester: e.target.value, shift_ids: [], subjects: [] }))}
                disabled={!selectedBatchId}
                className={selectClassName}
                required
              >
                <option value="">{selectedBatchId ? 'Select Semester' : 'Select batch first'}</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(sem => <option key={sem} value={sem}>Semester {sem}</option>)}
              </select>
            </div>
          </div>

          {formData.semester && (
            <div className="border-t border-gray-100 px-5 py-4">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_2fr]">
                <div><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Start date</p><p className="mt-1 font-semibold text-gray-900">{formData.start_date || 'Not configured'}</p></div>
                <div><p className="text-xs font-medium uppercase tracking-wide text-gray-500">End date</p><p className="mt-1 font-semibold text-gray-900">{formData.end_date || 'Not configured'}</p></div>
                <fieldset>
                  <legend className="text-xs font-medium uppercase tracking-wide text-gray-500">Shift <span className="text-red-500">*</span></legend>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
                    {shifts.map(shift => (
                      <label key={shift.id} className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <input type="checkbox" checked={formData.shift_ids.includes(shift.id)} onChange={(e) => setFormData(prev => ({ ...prev, shift_ids: e.target.checked ? [...prev.shift_ids, shift.id] : prev.shift_ids.filter(id => id !== shift.id) }))} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        {shift.name || shift.code}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
              {activeTermConfig && <p className="mt-4 text-xs text-gray-500">Dates are managed by {activeTermConfig.display_name}.</p>}
            </div>
          )}
        </section>


        {/* Subjects & Schedule Matrix */}
        {setupReady && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Subjects & Schedule Matrix</h2>
              <p className="text-sm text-gray-600 mt-1">
                Assign subjects to specific periods and days of the week
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {formData.batch_ids.length > 0 && formData.semester && formData.shift_ids.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddSubjectModal(true)}
                    disabled={loading}
                    className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 text-sm font-semibold"
                  >
                    <MdAdd />
                    <span>Add Subject Manually</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {formData.subjects.length > 0 ? (
            <div className="space-y-4">
              {/* Subject List with Delete Option */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Subjects ({formData.subjects.length})</h3>
                <div className="flex flex-wrap gap-2">
                  {formData.subjects.map((subject, index) => (
                    <div
                      key={subject.id || subject.subject_id || index}
                      className="flex items-center space-x-2 bg-white border border-gray-300 rounded-md px-3 py-2"
                    >
                      <span className="text-sm font-medium text-gray-800">
                        {subject.name || subject.subject_name}
                      </span>
                      {subject.is_shared && <SharedClassBadge />}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            subjects: prev.subjects.filter((_, i) => i !== index)
                          }));
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 transition"
                        title="Remove subject"
                      >
                        <MdDelete className="text-sm" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Schedule Matrix */}
              <SubjectScheduleMatrix
                subjects={formData.subjects}
                periods={periods}
                weekdays={weekdays}
                onSubjectsChange={(updatedSubjects) => {
                  setFormData(prev => ({
                    ...prev,
                    subjects: updatedSubjects
                  }));
                }}
                selectedShifts={shifts.filter(s => formData.shift_ids.includes(s.id))}
              />
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-gray-500">
              <p>{loading ? 'Loading subjects...' : 'No assigned subjects were found for this batch and semester.'}</p>
            </div>
          )}
        </div>
        )}

        {/* Submit Button */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/attendance/faculty-admin/timetables')}
            className="w-full sm:w-auto px-6 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !activeTermConfig}
            className="w-full sm:w-auto px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm sm:text-base shadow-md hover:shadow-lg"
          >
            <MdSave />
            <span>{saving ? 'Creating...' : 'Create Timetable'}</span>
          </button>
        </div>
      </form>

      {/* Add Subject Manually Modal */}
      <AddSubjectModal
        isOpen={showAddSubjectModal}
        onClose={() => setShowAddSubjectModal(false)}
        onAdd={(newSubjects) => {
          // Handle both single subject (backward compatibility) and array of subjects
          const subjectsArray = Array.isArray(newSubjects) ? newSubjects : [newSubjects];
          
          // Initialize period days arrays for each new subject
          const subjectsWithSchedule = subjectsArray.map(newSubject => {
            const subjectWithSchedule = { ...newSubject };
            periods.forEach((_, periodIdx) => {
              const periodNum = periodIdx + 1;
              subjectWithSchedule[`period_${periodNum}_days`] = [];
            });
            return subjectWithSchedule;
          });
          
          setFormData(prev => ({
            ...prev,
            subjects: [...prev.subjects, ...subjectsWithSchedule]
          }));
        }}
        batchIds={formData.batch_ids}
        campus={formData.campus}
        facultyId={selectedFacultyId}
        semester={formData.semester}
        existingSubjectIds={formData.subjects.map(s => s.subject_id || s.id).filter(Boolean)}
      />
    </div>
  );
}

export default CreateTimetable;
